/**
 * Thanksgiving stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingStickersPack: ToilePack = {
    slug: 'thanksgiving-stickers',
    category: 'season',
    name: 'Thanksgiving stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'turkey', label: 'Turkey', subject: 'A round turkey with a fanned tail wearing a small pilgrim hat, a thick white die-cut border'},
        {key: 'pie', label: 'Pumpkin pie', subject: 'A slice of pumpkin pie with a dollop of cream, a thick white die-cut border'},
        {key: 'leaf', label: 'Maple leaf', subject: 'A red maple leaf with a small smiling face, a thick white die-cut border'},
    ],
    styles: [
        {key: 'harvest', label: 'Harvest flat', style: 'flat harvest illustration, simplified shapes, no outlines, matte colour', palette: 'Mustard, rust, pumpkin orange, olive and cream', framing: 'Centered on a solid cream background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage americana', style: 'vintage americana illustration, halftone grain, slight misregistration, warm', palette: 'Faded rust, brown, mustard and cream', framing: 'Centered on an aged cream paper background with an even margin'},
        {key: 'watercolor', label: 'Watercolor', style: 'soft watercolour illustration, loose edges, paper texture showing', palette: 'Warm autumn washes of orange, ochre and brown', framing: 'Centered on a plain white paper background with soft visible edges'},
    ],
    directions: ['a cornucopia instead', 'as a smaller mini sheet', "for a kids' craft kit"],
    directionHint: 'try: a cornucopia instead',
    seo: {
        title: 'Thanksgiving Stickers: Turkey, Pie & Maple Leaf',
        metaDescription: 'Nine die-cut Thanksgiving stickers — turkey, pumpkin pie, maple leaf — in harvest flat, vintage americana, and watercolor styles.',
        h1: 'Thanksgiving stickers: turkey, pumpkin pie, maple leaf',
        tagline: 'Nine stickers, three motifs, three treatments each.',
        intro: 'Nine die-cut Thanksgiving stickers, each with a thick white border: a turkey in a pilgrim hat, a slice of pumpkin pie, and a maple leaf, in a flat harvest illustration, a vintage americana halftone, and a soft watercolor treatment. Ready to print and cut, or drop straight onto a digital planner.',
        useCases: [
            'a printable sticker sheet for a planner or journal',
            'a party favor bag or place-card seal',
            'a classroom craft or bulletin board',
            'a digital sticker for a tablet planning app',
        ],
        faq: [
            {q: 'Are these already die-cut, or do I cut them myself?', a: 'Each sticker is drawn with its white die-cut border built in, ready to print and cut out yourself with scissors or a cutting machine.'},
            {q: 'Can I print these at home?', a: 'Yes — each file is a clean isolated image on a plain background, sized to print at typical sticker-paper dimensions.'},
            {q: "What's the difference between the three styles?", a: 'Harvest flat uses simplified matte shapes, vintage americana adds halftone grain and warm faded color, and watercolor uses soft loose edges with visible paper texture.'},
            {q: 'Can I use these in a digital planner app?', a: 'Yes — the transparent-friendly PNGs drop straight into GoodNotes, Notability, or any app that accepts image stickers.'},
        ],
    },
}
